import { Typography } from 'antd'
import Card from 'components/card/Card'
import { IconArrowLeft } from 'components/icons'
import { thousandSeparator } from 'helpers/numbers'
import { useNavigate, useParams } from 'react-router-dom'
import SawitPoinLabel from './SawitPoinLabel'
import Banner from 'components/banner/Banner'
import ReceiverTable from './ReceiverTable'
import ModalActionSawitPoin from './ModalActionSawitPoin'
import ModalRejectSawitPoin from './ModalRejectSawitPoin'
import { useContext, useEffect } from 'react'

import useSawitPoin from '../hooks/useSawitPoin'
import dayjs from 'dayjs'
import { UserContext } from 'context/UserContext'
import { UserRoleEnum } from 'generated/api'
import Button from 'components/button/Button'
import Errors from 'components/errors/Errors'
import { regexUrl } from 'constants/regex'

function SawitPoinDetail() {
  const [state] = useContext(UserContext)
  const isFinanceLead = state.userInfo?.role === UserRoleEnum.FinanceLead
  const { id } = useParams()
  const navigate = useNavigate()

  const {
    fetchSawitPoinDetail,
    poinDetail,
    confirmRequestSawitPoin,
    loadingFetch,
    failedFetch,
    setModalConfirm,
    setModalReject,
    modalConfirm,
    modalReject,
  } = useSawitPoin()

  useEffect(() => {
    id && fetchSawitPoinDetail(id)
  }, [id])

  const linkedText = poinDetail.purpose.replace(
    regexUrl,
    url => `<a href="${url}" target="_blank">${url}</a>`,
  )

  return (
    <div className="content-wrapper">
      {!failedFetch && (
        <Card spinnerTestId="loader_payment" dataTestId="sawitpoin_detail" loading={loadingFetch}>
          <Card.Header className="mt-0">
            <button
              data-testid="btn_back_details"
              className="mr-4 bg-transparent border-0 cursor-pointer"
              onClick={() => navigate('/sawitpoin', { replace: true })}
            >
              {<IconArrowLeft />}
            </button>
            <Typography.Title className="m-0 text-2xl">Detail Permintaan</Typography.Title>
          </Card.Header>
          <div className="border-[0.5px] border-gray-200 mx-[-24px] border-solid" />
          <Card.Body className="flex flex-col">
            <div className="flex justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Typography className="m-0 font-semibold text-lg">Tanggal Permintaan</Typography>
                  <Typography className="font-bold text-xl">
                    {dayjs(poinDetail.requestDate).format('DD MMMM YYYY, HH:mm')}
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <Typography className="m-0 font-semibold text-lg">Total SawitPoin</Typography>
                  <Typography className="font-bold text-xl">
                    {thousandSeparator(poinDetail.totalPoint)}
                  </Typography>
                </div>
                <SawitPoinLabel state={poinDetail.status} />
                <div className="mt-[24px]" />
              </div>
              {isFinanceLead && poinDetail.status == 'WAITING' && (
                <div className="flex gap-2 h-fit">
                  <Button
                    data-testid="button-decline"
                    className="rounded-lg border px-6 py-3"
                    variant="outline-primary"
                    label="TOLAK"
                    onClick={() => setModalReject(true)}
                  />
                  <Button
                    data-testid="button-accept"
                    className="rounded-lg border px-6 py-3"
                    variant="solid-primary"
                    label="TERIMA"
                    onClick={() => setModalConfirm(true)}
                  />
                </div>
              )}
            </div>
            {poinDetail.status == 'REJECTED' && (
              <Banner
                info="Alasan Penolakan"
                subInfo={poinDetail.reason}
                iconColor="#FF4D4D"
                variant="error"
                icon
                className="mb-[24px]"
              />
            )}

            <div className="flex gap-4 justify-between w-full" data-testid="info-sawitpoin">
              <div className="flex-1 rounded-lg border-[#E5E5E7] border border-solid py-[12px] px-[16px]">
                <Typography className="text-lg font-bold mb-[18px]">Pemohon</Typography>
                <div className="flex justify-between">
                  <Typography>Nama: </Typography>
                  <Typography className="font-bold">{poinDetail.applicantsName}</Typography>
                </div>
                <div className="flex justify-between">
                  <Typography>Department: </Typography>
                  <Typography className="font-bold">{poinDetail.department}</Typography>
                </div>
              </div>
              <div className="flex-1 rounded-lg border-[#E5E5E7] border border-solid py-[12px] px-[16px]">
                <Typography className="text-lg font-bold mb-[18px]">Pencairan</Typography>
                <div className="flex justify-between grid grid-cols-2">
                  <Typography>Jenis Pencairan: </Typography>
                  <Typography className="font-bold text-end row-span-2">
                    {poinDetail.disbursementType}
                  </Typography>
                </div>
                <div className="flex justify-between grid grid-cols-2">
                  <Typography>Tujuan Pencairan: </Typography>
                  <Typography
                    className="font-bold text-end"
                    dangerouslySetInnerHTML={{ __html: linkedText }}
                  />
                </div>
                <div className="flex justify-between grid grid-cols-2">
                  <Typography>Judul Pencairan: </Typography>
                  <Typography className="font-bold text-end">{poinDetail.title}</Typography>
                </div>
                <div className="flex justify-between grid grid-cols-2">
                  <Typography>Deskripsi Pencairan: </Typography>
                  <Typography className="font-bold text-end">{poinDetail.description}</Typography>
                </div>
              </div>
            </div>
            <div
              className="flex flex-col gap-4 w-full mt-[24px] rounded-lg border-[#E5E5E7] border border-solid"
              data-testid="info-sawitpoin"
            >
              <div className="flex justify-between items-center w-full py-[12px] px-[16px]">
                <Typography className="text-lg font-bold">Penerima</Typography>
                <div className="flex items-center">
                  <Typography>Jumlah penerima: </Typography>
                  <Typography className="font-bold ml-2">
                    {poinDetail.totalRecipients} orang
                  </Typography>
                </div>
              </div>
              <ReceiverTable data={poinDetail.recipients} loading={loadingFetch} />
            </div>
          </Card.Body>
        </Card>
      )}
      <ModalActionSawitPoin
        isOpen={modalConfirm}
        setModal={setModalConfirm}
        onClick={confirmRequestSawitPoin}
        loading={loadingFetch}
      />
      <ModalRejectSawitPoin
        isOpen={modalReject}
        setModal={setModalReject}
        onClick={confirmRequestSawitPoin}
        loading={loadingFetch}
      />
      {failedFetch && (
        <Errors
          dataTestId="fetch_point"
          inverse={false}
          header="Mohon maaf, permintaan Anda tidak dapat diproses."
          paragraph="terjadi kesalahan internal pada server kami. Silakan coba beberapa saat lagi."
        />
      )}
    </div>
  )
}

export default SawitPoinDetail
